import React, { useEffect, useRef, useState } from 'react';

export default function LoadingScreen({ onDone, dark }) {
  const [progress, setProgress] = useState(0);
  const [fade, setFade] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    // Lock scrolling on mount
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    if (!dark) {
      // ── LIGHT THEME LOADING SEQUENCE (React spinner) ──
      let p = 0;
      const interval = setInterval(() => {
        p += Math.random() * 18 + 8;
        if (p >= 100) { p = 100; clearInterval(interval); }
        setProgress(Math.min(Math.round(p), 100));
      }, 120);

      const fadeTimer = setTimeout(() => setFade(true), 1800);
      const doneTimer = setTimeout(() => {
        onDone();
      }, 2200);

      return () => {
        clearInterval(interval);
        clearTimeout(fadeTimer);
        clearTimeout(doneTimer);
      };
    } else {
      // ── DARK THEME LOADING SEQUENCE (Asteroid Supernova Canvas) ──
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      let w = canvas.width = window.innerWidth;
      let h = canvas.height = window.innerHeight;

      const handleResize = () => {
        w = canvas.width = window.innerWidth;
        h = canvas.height = window.innerHeight;
      };
      window.addEventListener('resize', handleResize);

      // Twinkling stars
      const stars = [];
      for (let i = 0; i < 80; i++) {
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: Math.random() * 1.5 + 0.2,
          alpha: Math.random(),
          fade: (Math.random() * 0.02 + 0.005) * (Math.random() < 0.5 ? 1 : -1)
        });
      }

      const targetX = w / 2;
      const targetY = h / 2;
      const sunRad = 15;
      let sunGlow = 0;

      const asteroid = {
        x: -50,
        y: h * 0.3,
        r: 4,
        vx: 0,
        vy: 0,
        speed: 14,
        trail: []
      };

      const dx = targetX - asteroid.x;
      const dy = targetY - asteroid.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      asteroid.vx = (dx / dist) * asteroid.speed;
      asteroid.vy = (dy / dist) * asteroid.speed;

      let supernovaTime = -1;
      let supernovaRadius = 0;
      let supernovaFlash = 0;
      let isDoneCalled = false;

      let frameId;

      const drawLoader = () => {
        ctx.fillStyle = '#030407';
        ctx.fillRect(0, 0, w, h);

        // Draw Twinkling background stars
        stars.forEach(s => {
          s.alpha += s.fade;
          if (s.alpha > 1 || s.alpha < 0) s.fade = -s.fade;
          ctx.fillStyle = `rgba(255, 255, 255, ${Math.max(0, s.alpha)})`;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fill();
        });

        if (supernovaTime === -1) {
          // Central glowing sun
          sunGlow += 0.05;
          const glowScale = 1 + Math.sin(sunGlow) * 0.15;
          const g = ctx.createRadialGradient(targetX, targetY, 0, targetX, targetY, sunRad * 3 * glowScale);
          g.addColorStop(0, 'rgba(255, 240, 180, 0.9)');
          g.addColorStop(0.3, 'rgba(212, 168, 67, 0.5)');
          g.addColorStop(0.8, 'rgba(212, 168, 67, 0.1)');
          g.addColorStop(1, 'rgba(0, 0, 0, 0)');

          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(targetX, targetY, sunRad * 3 * glowScale, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(targetX, targetY, sunRad, 0, Math.PI * 2);
          ctx.fill();

          // Asteroid travel
          asteroid.x += asteroid.vx;
          asteroid.y += asteroid.vy;
          asteroid.trail.push({ x: asteroid.x, y: asteroid.y });
          if (asteroid.trail.length > 20) asteroid.trail.shift();

          // Draw trail
          ctx.beginPath();
          for (let i = 0; i < asteroid.trail.length; i++) {
            ctx.lineTo(asteroid.trail[i].x, asteroid.trail[i].y);
          }
          ctx.strokeStyle = 'rgba(212, 168, 67, 0.6)';
          ctx.lineWidth = asteroid.r * 1.5;
          ctx.lineCap = 'round';
          ctx.stroke();

          // Draw head
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(asteroid.x, asteroid.y, asteroid.r, 0, Math.PI * 2);
          ctx.fill();

          // Collision check
          const cDx = targetX - asteroid.x;
          const cDy = targetY - asteroid.y;
          const cDist = Math.sqrt(cDx * cDx + cDy * cDy);
          if (cDist < sunRad + 2) {
            supernovaTime = 0; // Supernova triggers
          }
        } else {
          supernovaTime++;
          if (supernovaTime < 50) {
            // Explode
            supernovaRadius += (Math.max(w, h) - supernovaRadius) * 0.1;
            const g = ctx.createRadialGradient(targetX, targetY, 0, targetX, targetY, supernovaRadius);
            g.addColorStop(0, '#ffffff');
            g.addColorStop(0.1, 'rgba(255, 230, 150, 0.9)');
            g.addColorStop(0.4, 'rgba(212, 168, 67, 0.6)');
            g.addColorStop(0.8, 'rgba(180, 100, 30, 0.2)');
            g.addColorStop(1, 'rgba(0, 0, 0, 0)');

            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(targetX, targetY, supernovaRadius, 0, Math.PI * 2);
            ctx.fill();

            // Particles
            for (let i = 0; i < 40; i++) {
              const angle = (i / 40) * Math.PI * 2 + supernovaTime * 0.1;
              const pDist = supernovaTime * 8;
              const px = targetX + Math.cos(angle) * pDist;
              const py = targetY + Math.sin(angle) * pDist;
              ctx.fillStyle = `rgba(255, 240, 180, ${1 - supernovaTime / 50})`;
              ctx.beginPath();
              ctx.arc(px, py, 3, 0, Math.PI * 2);
              ctx.fill();
            }
          } else if (supernovaTime >= 50 && supernovaTime < 90) {
            // Flash screen
            supernovaFlash += 0.025;
            ctx.fillStyle = `rgba(255, 255, 255, ${Math.min(1, supernovaFlash)})`;
            ctx.fillRect(0, 0, w, h);
          } else {
            // Done!
            setFade(true);
            if (!isDoneCalled) {
              isDoneCalled = true;
              setTimeout(() => {
                onDone();
              }, 800);
            }
          }
        }

        if (supernovaTime < 95) {
          frameId = requestAnimationFrame(drawLoader);
        }
      };

      drawLoader();

      // Safe Fallback
      const fallback = setTimeout(() => {
        setFade(true);
        if (!isDoneCalled) {
          isDoneCalled = true;
          setTimeout(() => {
            onDone();
          }, 800);
        }
      }, 4200);

      return () => {
        window.removeEventListener('resize', handleResize);
        cancelAnimationFrame(frameId);
        clearTimeout(fallback);
      };
    }
  }, [dark, onDone]);

  // Light Mode Layout
  if (!dark) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: '#FAFAF8',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '2.5rem',
        opacity: fade ? 0 : 1,
        transition: 'opacity 0.5s ease',
        pointerEvents: fade ? 'none' : 'all',
      }}>
        <div style={{ position: 'relative', width: 120, height: 120 }}>
          <svg width="120" height="120" viewBox="0 0 120 120" style={{ position: 'absolute', inset: 0, animation: 'spinRing 3s linear infinite' }}>
            <circle cx="60" cy="60" r="54" fill="none" stroke="var(--accent-glow)" strokeWidth="1.5" />
            <circle cx="60" cy="60" r="54" fill="none" stroke="var(--accent-color)" strokeWidth="1.5"
              strokeDasharray="80 260" strokeLinecap="round" />
          </svg>
          <svg width="120" height="120" viewBox="0 0 120 120" style={{ position: 'absolute', inset: 0, animation: 'spinRingReverse 4s linear infinite' }}>
            <circle cx="60" cy="60" r="46" fill="none" stroke="rgba(0,212,170,0.1)" strokeWidth="1" />
            <circle cx="60" cy="60" r="46" fill="none" stroke="#00D4AA" strokeWidth="1"
              strokeDasharray="40 250" strokeLinecap="round" />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
              fontFamily: "'Syne', sans-serif", fontSize: '2rem', fontWeight: 800,
              background: 'linear-gradient(135deg, var(--accent-color), var(--accent2-color))',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.03em',
            }}>VG</div>
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '1.1rem', fontWeight: 700, color: '#0A0A0A', letterSpacing: '0.05em' }}>Vikash Gautam</div>
          <div style={{ fontFamily: "'Space Mono',monospace", fontSize: '0.72rem', color: '#6B7280', marginTop: 4, letterSpacing: '0.15em' }}>// loading portfolio</div>
        </div>

        <div style={{ width: 200, height: 2, background: 'rgba(10,10,10,0.08)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(to right, var(--accent-color), var(--accent2-color))',
            borderRadius: 2,
            width: progress + '%',
            transition: 'width 0.15s ease',
          }} />
        </div>
        <div style={{ fontFamily: "'Space Mono',monospace", fontSize: '0.7rem', color: '#6B7280', marginTop: -20 }}>{progress}%</div>

        <style>{`
          @keyframes spinRing { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes spinRingReverse { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
        `}</style>
      </div>
    );
  }

  // Dark Mode Layout
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: '#030407',
      opacity: fade ? 0 : 1,
      transition: 'opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
      pointerEvents: fade ? 'none' : 'all',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
    }}>
      <canvas ref={canvasRef} style={{ width: '100vw', height: '100vh', position: 'absolute', inset: 0, background: '#030407' }} />
      <div style={{
        position: 'absolute',
        bottom: '12%',
        fontFamily: "'Space Mono', monospace",
        fontSize: '11px',
        letterSpacing: '5px',
        color: '#d4a843',
        textShadow: '0 0 10px rgba(212, 168, 67, 0.5)',
        animation: 'loaderPulse 1.6s ease-in-out infinite',
        zIndex: 10001,
        textAlign: 'center',
        pointerEvents: 'none'
      }}>
        INITIALIZING QUANTUM PORTFOLIO...
      </div>
      <style>{`
        @keyframes loaderPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; text-shadow: 0 0 20px rgba(212, 168, 67, 0.8); }
        }
      `}</style>
    </div>
  );
}
